import { useState, useRef, useEffect } from 'react';
import {
  Send, Sparkles, UserCheck, Bot, FileText, MessageCircle, CalendarClock, CheckCircle2,
} from 'lucide-react';

type Message = { id: string; from: 'user' | 'ai' | 'firm'; text: string; time: string; forwarded?: boolean };

const seed: Message[] = [
  { id: '1', from: 'ai', text: 'Hi Bashir - I can help with anything about your business, taxes or files. Where would you like to start?', time: '9:42 AM' },
  { id: '2', from: 'user', text: 'How much GST did I pay last quarter?', time: '9:43 AM' },
  { id: '3', from: 'ai', text: 'Q3 (Jan-Mar 2026): $4,820 in GST - 12% lower than Q2 thanks to equipment GST credits.', time: '9:43 AM' },
  { id: '4', from: 'user', text: 'Can you help me understand my super contributions?', time: '9:45 AM' },
  { id: '5', from: 'ai', text: "That's better answered by your accountant directly - I've forwarded it to Hamid. He'll get back to you shortly.", time: '9:45 AM', forwarded: true },
];

const timelineEvents = [
  { id: 't1', icon: 'message', title: 'Hamid sent you a message', detail: 'About your March P&L equipment purchase…', time: '2h ago' },
  { id: 't2', icon: 'file', title: 'New file shared', detail: 'Tax Return FY2024-25.pdf', time: '10:14 AM' },
  { id: 't3', icon: 'done', title: 'You uploaded receipts', detail: 'Marked complete', time: 'Yesterday' },
  { id: 't4', icon: 'deadline', title: 'Q3 BAS reminder', detail: 'Due in 12 days', time: '2d ago' },
  { id: 't5', icon: 'message', title: 'You asked about super', detail: 'Forwarded to Hamid', time: '3d ago' },
  { id: 't6', icon: 'file', title: 'New file shared', detail: 'P&L · March 2026', time: '8 Apr' },
] as const;

export default function HelpPageV2() {
  const [tab, setTab] = useState<'chat' | 'timeline'>('chat');
  const [messages, setMessages] = useState<Message[]>(seed);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, thinking]);

  const send = (text: string) => {
    if (!text.trim()) return;
    const time = new Date().toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
    setMessages(prev => [...prev, { id: String(Date.now()), from: 'user', text: text.trim(), time }]);
    setInput('');
    setThinking(true);
    setTimeout(() => {
      const lower = text.toLowerCase();
      const escalate = lower.includes('super') || lower.includes('audit') || lower.includes('legal');
      setMessages(prev => [...prev, escalate
        ? { id: String(Date.now() + 1), from: 'ai', text: "Best answered by Hamid directly - forwarded.", time, forwarded: true }
        : { id: String(Date.now() + 1), from: 'ai', text: 'Based on your most recent figures, here\'s what I can see - happy to dig deeper.', time }]);
      setThinking(false);
    }, 900);
  };

  return (
    <div className="flex h-[calc(100dvh-200px)] flex-col pt-4">
      <div>
        <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-white/40">Direct line</p>
        <h1 className="mt-1 text-3xl font-black tracking-tighter">Concierge</h1>
      </div>

      {/* Glass tabs */}
      <div className="mt-4 flex rounded-2xl border border-white/10 bg-white/5 p-1 backdrop-blur-md">
        {[
          { k: 'chat', label: 'Chat', Icon: MessageCircle },
          { k: 'timeline', label: 'Activity', Icon: CalendarClock },
        ].map(({ k, label, Icon }) => (
          <button
            key={k}
            onClick={() => setTab(k as any)}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl px-2 py-2 text-xs font-semibold transition-all ${
              tab === k
                ? 'bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-lg shadow-violet-500/30'
                : 'text-white/50 hover:text-white/80'
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </div>

      {tab === 'timeline' && (
        <div className="mt-4 flex-1 overflow-y-auto pb-4">
          <div className="relative space-y-3 ps-7">
            <span className="absolute inset-y-2 start-3 w-px bg-gradient-to-b from-violet-400/50 via-white/10 to-transparent" />
            {timelineEvents.map(ev => {
              const map = {
                file: { Icon: FileText, color: 'from-violet-500 to-fuchsia-500' },
                message: { Icon: MessageCircle, color: 'from-cyan-400 to-blue-500' },
                deadline: { Icon: CalendarClock, color: 'from-amber-400 to-orange-500' },
                done: { Icon: CheckCircle2, color: 'from-emerald-400 to-cyan-500' },
              };
              const { Icon, color } = map[ev.icon as keyof typeof map];
              return (
                <div key={ev.id} className="relative">
                  <div className={`absolute -start-[1.85rem] top-1 flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br ${color} shadow-lg ring-4 ring-[#2a2a3a]`}>
                    <Icon className="h-3.5 w-3.5 text-white" />
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3.5 backdrop-blur-md">
                    <p className="text-sm font-semibold">{ev.title}</p>
                    <p className="mt-0.5 text-[11px] leading-relaxed text-white/60">{ev.detail}</p>
                    <p className="mt-1 text-[10px] uppercase tracking-wider text-white/30">{ev.time}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === 'chat' && (
        <>
          <div ref={scrollRef} className="mt-4 flex-1 space-y-3 overflow-y-auto pb-3">
            {messages.map(m => <Bubble key={m.id} msg={m} />)}
            {thinking && (
              <div className="flex items-end gap-2">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500">
                  <Bot className="h-4 w-4 text-white" />
                </div>
                <div className="rounded-2xl rounded-bl-sm border border-white/10 bg-white/5 px-4 py-2.5 backdrop-blur-md">
                  <div className="flex gap-1">
                    {[0, 150, 300].map(d => (
                      <span key={d} className="h-2 w-2 animate-bounce rounded-full bg-white/60" style={{ animationDelay: `${d}ms` }} />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <form
            onSubmit={e => { e.preventDefault(); send(input); }}
            className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 p-2 backdrop-blur-md"
          >
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Ask anything…"
              className="flex-1 bg-transparent px-3 py-2 text-sm placeholder:text-white/40 focus:outline-none"
            />
            <button
              type="submit"
              disabled={!input.trim()}
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white transition-all hover:shadow-lg hover:shadow-violet-500/30 disabled:opacity-30"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </>
      )}
    </div>
  );
}

function Bubble({ msg }: { msg: Message }) {
  if (msg.from === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[78%] rounded-2xl rounded-br-sm bg-gradient-to-br from-violet-500 to-fuchsia-500 px-4 py-2.5 text-sm text-white shadow-lg shadow-violet-500/20">
          {msg.text}
          <p className="mt-1 text-[10px] opacity-70">{msg.time}</p>
        </div>
      </div>
    );
  }
  const isFirm = msg.from === 'firm';
  return (
    <div className="flex items-end gap-2">
      <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${isFirm ? 'from-emerald-400 to-cyan-500' : 'from-violet-500 to-fuchsia-500'}`}>
        {isFirm ? <UserCheck className="h-4 w-4 text-white" /> : <Sparkles className="h-4 w-4 text-white" />}
      </div>
      <div className="max-w-[78%]">
        <div className="rounded-2xl rounded-bl-sm border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white/90 backdrop-blur-md">
          {msg.text}
        </div>
        <div className="mt-1 flex items-center gap-2 px-1">
          <span className="text-[10px] text-white/40">{msg.time}</span>
          {msg.forwarded && (
            <span className="flex items-center gap-1 rounded-full bg-emerald-400/15 px-2 py-0.5 text-[10px] font-medium text-emerald-300">
              <UserCheck className="h-2.5 w-2.5" />
              Sent to Hamid
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
